import { Avatar, Colors, Container, defaultHover, Text } from '@amelio/ui-library';
import { AnimatePresence, motion } from 'motion/react';

import type { User } from '@/types/directory/User';

import { UserCard } from '@/components/UserCard/UserCard';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { getFullName, getProfilePictureUrl } from '@/utils/user';

interface SelectRevieweeResultsProps {
    hasNoReviewees: boolean;
    reviewees: User[];
    selectedReviewees: User[];
    selectReviewee: (user: User) => void;
}

export const SelectRevieweeResults = ({
    hasNoReviewees,
    reviewees,
    selectedReviewees,
    selectReviewee,
}: SelectRevieweeResultsProps) => {
    const { t } = useAmelioTranslations();
    const { getJobTitle } = useCompanyInformation();
    const noEmployeesFoundTextKey =
        'evaluation360SelectReviewee.selectReviewee.noEmployeesFound' as Parameters<typeof t>[0];

    const isRevieweeSelected = (reviewee: User) =>
        selectedReviewees.some((selectedReviewee) => selectedReviewee.id === reviewee.id);

    if (hasNoReviewees) {
        return <Text align={'center'}>{t(noEmployeesFoundTextKey)}</Text>;
    }

    return (
        <Container className={'responsive-grid-1-2-3-2xl w-full gap-6'}>
            <AnimatePresence mode={'wait'}>
                {reviewees.map((reviewee) => (
                    <motion.div
                        key={reviewee.id}
                        className={'w-full min-w-0'}
                        layout
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.6, 0, 0.6, 0.5] }}
                    >
                        <UserCard
                            className={`${defaultHover} w-full`}
                            alt={getFullName(reviewee)}
                            avatar={
                                <Avatar
                                    alt={getFullName(reviewee)}
                                    src={getProfilePictureUrl(reviewee?.profilePictureUrl || '')}
                                />
                            }
                            description={getJobTitle(reviewee.jobTitleId || '')}
                            name={getFullName(reviewee)}
                            backgroundColor={Colors.WHITE_80}
                            borderColor={
                                isRevieweeSelected(reviewee) ? Colors.PRIMARY : Colors.WHITE
                            }
                            borderSize={'border'}
                            descriptionTextColor={Colors.BASE_400}
                            onClick={() => selectReviewee(reviewee)}
                        />
                    </motion.div>
                ))}
            </AnimatePresence>
        </Container>
    );
};
